/* Companies (Sales · Stap 2)
 *
 * Two views:
 *   • LIST   — verrijkte company-cards (default)
 *   • MAP    — Stakeholder power-map (influence × sentiment grid) voor
 *              de geselecteerde company. Toont waar elke contactpersoon
 *              zit op de Champion ↔ Blocker / Warm ↔ Cold matrix.
 *
 * Plus a "+ Nieuwe Company" modal that creates a manual lead with
 * stakeholders attached (is_manual=true server-side).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "../Dashboard";
import { LinkedInButton } from "@/components/common/LinkedInButton";
import {
    Building2, ArrowRight, Plus, X, List as ListIcon, Network,
    UserPlus, Trash2, Linkedin, Mail, Loader2, Sparkles, UserCheck,
    Users, ExternalLink, ChevronRight, ChevronDown, ChevronUp, ChevronsUpDown, Database, Info,
    Pencil, Save,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import i18n from "i18next";

const STAGE_COLOR = {
    new: "text-[var(--jr-text-2)] bg-white/5",
    contacted: "text-[var(--jr-info)] bg-[var(--jr-info)]/10",
    engaged: "text-[var(--jr-amber)] bg-[var(--jr-amber)]/10",
    meeting: "text-[var(--jr-brand)] bg-[var(--jr-brand)]/10",
    won: "text-[var(--jr-success)] bg-[var(--jr-success)]/15",
    lost: "text-[var(--jr-error)] bg-[var(--jr-error)]/10",
};

const INFLUENCE = [
    { id: "champion",       label: "Champion",        accent: "var(--jr-success)" },
    { id: "decision_maker", label: "Decision maker",  accent: "var(--jr-brand)" },
    { id: "neutral",        label: "Neutraal",         accent: "var(--jr-text-2)" },
    { id: "blocker",        label: "Blocker",          accent: "var(--jr-error)" },
];
const SENTIMENT = [
    { id: "warm",    label: "Warm",     accent: "var(--jr-success)" },
    { id: "neutral", label: "Neutraal", accent: "var(--jr-text-2)" },
    { id: "cold",    label: "Koud",     accent: "var(--jr-info)" },
];

export default function SalesCompanies() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [view, setView] = useState("list");
    const [showCreate, setShowCreate] = useState(false);
    const [editing, setEditing] = useState(null);     // Company being edited (null = closed)
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        try { const r = await api.get("/leads"); setRows(r.data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const deleteCompany = async (lead) => {
        if (!window.confirm(i18n.t("companies.delete_confirm", { company: lead.company_name }))) return;
        try {
            const { data } = await api.delete(`/leads/${lead.id}`);
            toast.success(data?.outreach_deleted ? i18n.t("companies.deleted_with", { count: data.outreach_deleted }) : i18n.t("common.deleted"));
            if (selected?.id === lead.id) setSelected(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("companies.delete_failed"));
        }
    };

    const filtered = q
        ? rows.filter((r) => (r.company_name + " " + (r.contact_name || "")).toLowerCase().includes(q.toLowerCase()))
        : rows;

    // Keep selected reference fresh when rows mutate
    const selectedRow = selected ? rows.find((r) => r.id === selected.id) || selected : null;

    return (
        <PageHeader
            title="Companies"
            subtitle={i18n.t("companies.subtitle")}
            actions={
                <div className="flex items-center gap-2">
                    <ViewToggle view={view} setView={setView} />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={i18n.t("companies.ph_search")} data-testid="companies-search"
                        className="bg-[var(--jr-surface)] border border-white/10 rounded-full px-4 py-2 text-sm w-56 focus:border-[var(--jr-brand)] outline-none" />
                    <button onClick={() => setShowCreate(true)} data-testid="company-create-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)]">
                        <Plus size={14} /> {i18n.t("companies.new_company")}
                    </button>
                </div>
            }
        >
            {loading ? (
                <div className="text-[var(--jr-text-2)]">{i18n.t("common.loading")}</div>
            ) : filtered.length === 0 ? (
                <EmptyState onCreate={() => setShowCreate(true)} />
            ) : view === "list" ? (
                <CompanyList rows={filtered} onOpenMap={(r) => { setSelected(r); setView("map"); }} onEdit={setEditing} onDelete={deleteCompany} onChanged={load} />
            ) : (
                <StakeholderMap
                    rows={filtered}
                    selected={selectedRow}
                    setSelected={setSelected}
                    onSaved={load}
                    onEdit={setEditing}
                    onDelete={deleteCompany}
                />
            )}

            {showCreate && (
                <CompanyCreateModal
                    onClose={() => setShowCreate(false)}
                    onCreated={async (lead) => { setShowCreate(false); await load(); setSelected(lead); }}
                />
            )}
            {editing && (
                <CompanyEditModal
                    lead={editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { setEditing(null); await load(); }}
                />
            )}
        </PageHeader>
    );
}

/* ------------------------------------------------------------------ */
/*  View toggle                                                        */
/* ------------------------------------------------------------------ */
function ToggleBtn({ id, Icon, label, current, setView }) {
    return (
        <button onClick={() => setView(id)} data-testid={`view-${id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-[0.16em] transition-colors ${current === id ? "bg-white/[0.08] text-white" : "text-[var(--jr-text-2)] hover:text-white"}`}>
            <Icon size={12} /> {label}
        </button>
    );
}

function ViewToggle({ view, setView }) {
    return (
        <div className="inline-flex rounded-full border border-white/10 bg-[var(--jr-surface)] overflow-hidden">
            <ToggleBtn id="list" Icon={ListIcon} label={i18n.t("companies.view_list")} current={view} setView={setView} />
            <ToggleBtn id="map" Icon={Network} label="Stakeholder map" current={view} setView={setView} />
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Empty state                                                        */
/* ------------------------------------------------------------------ */
function EmptyState({ onCreate }) {
    return (
        <div className="rounded-xl border border-dashed border-white/15 bg-[var(--jr-surface)] p-10 text-center">
            <Building2 className="text-[var(--jr-brand)] mx-auto" size={28} />
            <p className="mt-4 text-[var(--jr-text-2)]">
                {i18n.t("companies.empty_pre")}{" "}
                <Link to="/app/sales/signals" className="text-[var(--jr-brand)] hover:underline">Signal Hunting</Link>{" "}
                {i18n.t("companies.empty_post")}
            </p>
            <button onClick={onCreate} data-testid="company-empty-create"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)]">
                <Plus size={14} /> {i18n.t("companies.first_company")}
            </button>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  List view — Excel-achtige tabel met sortering + multi-select       */
/* ------------------------------------------------------------------ */
function SortTh({ label, sortKey, sort, onSort, testid }) {
    const active = sort.key === sortKey;
    return (
        <th className="px-3 py-2.5 select-none">
            <button onClick={() => onSort(sortKey)} data-testid={testid}
                className={`inline-flex items-center gap-1 uppercase tracking-[0.14em] text-[10px] font-mono transition-colors ${active ? "text-white" : "text-[var(--jr-text-3)] hover:text-white"}`}>
                {label}
                {active ? (sort.dir === 1 ? <ChevronUp size={11} /> : <ChevronDown size={11} />) : <ChevronsUpDown size={11} className="opacity-40" />}
            </button>
        </th>
    );
}

function Checkbox({ checked, onClick, testid, label }) {
    return (
        <button onClick={onClick} data-testid={testid} aria-label={label} title={label}
            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${checked ? "bg-[var(--jr-brand)] border-[var(--jr-brand)]" : "border-white/25 hover:border-white/50"}`}>
            {checked && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        </button>
    );
}

function CompanyList({ rows, onOpenMap, onEdit, onDelete, onChanged }) {
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [sort, setSort] = useState({ key: "company_name", dir: 1 });

    const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

    const sorted = useMemo(() => {
        const arr = [...rows];
        const { key, dir } = sort;
        arr.sort((a, b) => {
            let va, vb;
            if (key === "stakeholders") { va = (a.stakeholders || []).length; vb = (b.stakeholders || []).length; }
            else if (key === "score") { va = a.score || 0; vb = b.score || 0; }
            else { va = (a[key] || "").toString().toLowerCase(); vb = (b[key] || "").toString().toLowerCase(); }
            return va < vb ? -dir : va > vb ? dir : 0;
        });
        return arr;
    }, [rows, sort]);

    const toggleSelect = (id) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const allSelected = sorted.length > 0 && sorted.every((r) => selected.has(r.id));
    const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(sorted.map((r) => r.id)));

    const bulkDelete = async () => {
        if (!window.confirm(i18n.t("companies.bulk_delete_confirm", { n: selected.size }))) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/leads/bulk-delete", { lead_ids: [...selected].slice(0, 100) });
            toast.success(i18n.t("companies.bulk_deleted_toast", { n: data.deleted }));
            setSelected(new Set());
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("companies.delete_failed")); }
        finally { setBulkBusy(false); }
    };

    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] overflow-hidden">
            {selected.size > 0 && (
                <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--jr-brand)]/25 bg-[var(--jr-brand)]/[0.07]"
                    data-testid="companies-bulk-bar">
                    <span className="text-xs text-[var(--jr-text-2)]">{i18n.t("companies.selected_n", { n: selected.size })}</span>
                    <button onClick={bulkDelete} disabled={bulkBusy} data-testid="companies-bulk-delete"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-[#ef4444]/40 bg-[#ef4444]/10 text-[#ef4444] hover:bg-[#ef4444]/20 disabled:opacity-50">
                        {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                        {i18n.t("companies.bulk_delete", { n: selected.size })}
                    </button>
                    <button onClick={() => setSelected(new Set())} data-testid="companies-bulk-clear"
                        className="ml-auto text-xs text-[var(--jr-text-3)] hover:text-white">
                        {i18n.t("common.cancel")}
                    </button>
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left border-b border-white/8 bg-white/[0.02]">
                            <th className="pl-4 pr-1 py-2.5 w-8">
                                <Checkbox checked={allSelected} onClick={toggleSelectAll}
                                    testid="companies-select-all" label={i18n.t("companies.select_all")} />
                            </th>
                            <SortTh label={i18n.t("companies.col_company")} sortKey="company_name" sort={sort} onSort={toggleSort} testid="sort-company" />
                            <SortTh label={i18n.t("companies.col_contact")} sortKey="contact_name" sort={sort} onSort={toggleSort} testid="sort-contact" />
                            <SortTh label={i18n.t("companies.col_title")} sortKey="contact_title" sort={sort} onSort={toggleSort} testid="sort-title" />
                            <SortTh label="Stage" sortKey="stage" sort={sort} onSort={toggleSort} testid="sort-stage" />
                            <SortTh label="ICP" sortKey="score" sort={sort} onSort={toggleSort} testid="sort-icp" />
                            <SortTh label={i18n.t("companies.col_stakeholders")} sortKey="stakeholders" sort={sort} onSort={toggleSort} testid="sort-stakeholders" />
                            <th className="px-3 py-2.5 text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)]">{i18n.t("companies.col_notes")}</th>
                            <th className="px-3 py-2.5"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((r) => {
                            const manual = !!r.is_manual;
                            const stCount = (r.stakeholders || []).length;
                            const isSel = selected.has(r.id);
                            return (
                                <tr key={r.id} data-testid={`company-${r.id}`}
                                    className={`border-b border-white/5 transition-colors ${isSel ? "bg-[var(--jr-brand)]/[0.06]" : "hover:bg-white/[0.02]"}`}>
                                    <td className="pl-4 pr-1 py-2.5 w-8">
                                        <Checkbox checked={isSel} onClick={() => toggleSelect(r.id)}
                                            testid={`company-select-${r.id}`} label={i18n.t("companies.select_one")} />
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className={`w-6 h-6 rounded grid place-items-center shrink-0 ${manual ? "bg-[var(--jr-brand)]/25 text-[var(--jr-brand)]" : "bg-[var(--jr-brand)]/12 text-[var(--jr-brand)]"}`}>
                                                <Building2 size={12} />
                                            </div>
                                            <span className="text-white font-medium truncate max-w-[220px]" title={r.company_name}>{r.company_name}</span>
                                            {manual && (
                                                <span data-testid={`manual-badge-${r.id}`}
                                                    className="text-[8px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[var(--jr-brand)]/20 text-[var(--jr-brand)] border border-[var(--jr-brand)]/40 shrink-0">
                                                    Manual
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2.5 text-xs text-[var(--jr-text-2)] truncate max-w-[140px]">{r.contact_name || "—"}</td>
                                    <td className="px-3 py-2.5 text-xs text-[var(--jr-text-2)] truncate max-w-[160px]" title={r.contact_title || ""}>{r.contact_title || "—"}</td>
                                    <td className="px-3 py-2.5">
                                        <span className={`text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-0.5 rounded ${STAGE_COLOR[r.stage] || "bg-white/5"}`}>{r.stage}</span>
                                    </td>
                                    <td className="px-3 py-2.5 text-xs font-mono text-[var(--jr-text-2)]">{r.score ?? "—"}</td>
                                    <td className="px-3 py-2.5">
                                        <button onClick={() => onOpenMap(r)} data-testid={`open-map-${r.id}`}
                                            className="text-xs text-[var(--jr-info)] hover:text-white inline-flex items-center gap-1">
                                            <Network size={11} /> {stCount}
                                        </button>
                                    </td>
                                    <td className="px-3 py-2.5 text-xs text-[var(--jr-text-3)] truncate max-w-[240px]" title={r.notes || ""}>{r.notes || "—"}</td>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center gap-0.5 justify-end">
                                            <Link to="/app/sales/outreach" title="Start outreach" data-testid={`company-outreach-${r.id}`}
                                                className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-[var(--jr-brand)] hover:bg-[var(--jr-brand)]/10 transition-colors">
                                                <ArrowRight size={13} />
                                            </Link>
                                            <button onClick={() => onEdit?.(r)} data-testid={`company-edit-${r.id}`}
                                                title={i18n.t("companies.edit_tip")} aria-label={i18n.t("common.edit")}
                                                className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-white hover:bg-white/8 transition-colors">
                                                <Pencil size={13} />
                                            </button>
                                            <button onClick={() => onDelete?.(r)} data-testid={`company-delete-${r.id}`}
                                                title={i18n.t("companies.delete_tip")} aria-label={i18n.t("common.delete")}
                                                className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-red-400 hover:bg-red-500/10 transition-colors">
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Stakeholder Map view                                               */
/* ------------------------------------------------------------------ */
function StakeholderMap({ rows, selected, setSelected, onSaved, onEdit, onDelete }) {
    const sel = selected || rows[0];
    useEffect(() => { if (!selected && rows[0]) setSelected(rows[0]); }, [rows, selected, setSelected]);
    if (!sel) return null;

    return (
        <div className="grid lg:grid-cols-[260px_1fr] gap-6">
            <aside className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4 max-h-[640px] overflow-y-auto">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-3">{rows.length} companies</div>
                <div className="space-y-1">
                    {rows.map((c) => {
                        const stCount = (c.stakeholders || []).length;
                        const isSel = sel.id === c.id;
                        return (
                            <div key={c.id} className={`group rounded-lg border transition-colors ${isSel ? "bg-[var(--jr-brand)]/15 border-[var(--jr-brand)]/40" : "border-transparent hover:bg-white/5"}`}>
                                <button onClick={() => setSelected(c)} data-testid={`map-pick-${c.id}`}
                                    className="w-full text-left px-3 py-2">
                                    <div className="flex items-center gap-2">
                                        <div className="text-sm font-medium text-white truncate flex-1">{c.company_name}</div>
                                        {c.is_manual && <span className="text-[9px] font-mono text-[var(--jr-brand)]">MAN</span>}
                                    </div>
                                    <div className="text-[11px] text-[var(--jr-text-3)] truncate">{stCount} stakeholder{stCount === 1 ? "" : "s"}</div>
                                </button>
                                {isSel && (
                                    <div className="flex items-center gap-1 px-3 pb-2">
                                        <button onClick={() => onEdit?.(c)} data-testid={`map-edit-${c.id}`}
                                            className="flex-1 inline-flex items-center justify-center gap-1 py-1 rounded text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                                            <Pencil size={10}/> Bewerk
                                        </button>
                                        <button onClick={() => onDelete?.(c)} data-testid={`map-delete-${c.id}`}
                                            className="flex-1 inline-flex items-center justify-center gap-1 py-1 rounded text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)] hover:text-red-400 hover:bg-red-500/10">
                                            <Trash2 size={10}/> Verwijder
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </aside>
            <StakeholderGrid lead={sel} onSaved={onSaved} />
        </div>
    );
}

function PowerCell({ inf, sn, here, leadId, onDropped }) {
    const [over, setOver] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!over) setOver(true);
    };
    const handleDragLeave = () => setOver(false);
    const handleDrop = async (e) => {
        e.preventDefault();
        setOver(false);
        try {
            const payload = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
            if (!payload.id) return;
            // Skip no-op moves (dropped in the same cell)
            if (payload.influence === inf.id && payload.sentiment === sn.id) return;
            setSaving(true);
            await api.patch(
                `/leads/${leadId}/stakeholders/${payload.id}?influence=${inf.id}&sentiment=${sn.id}`
            );
            toast.success(`${payload.name || "Stakeholder"} → ${inf.label} · ${sn.label}`);
            onDropped?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || i18n.t("companies.move_failed"));
        } finally {
            setSaving(false);
        }
    };
    const handleDragStart = (e, p) => {
        e.dataTransfer.setData("application/json", JSON.stringify({
            id: p.id, name: p.name, influence: inf.id, sentiment: sn.id,
        }));
        e.dataTransfer.effectAllowed = "move";
    };

    return (
        <div
            data-testid={`cell-${inf.id}-${sn.id}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`rounded-lg border ${over ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/8" : "border-white/8 bg-[var(--jr-surface-2)]"} min-h-[88px] p-2 flex flex-col gap-1.5 transition-colors ${saving ? "opacity-70" : ""}`}
        >
            {here.length === 0 ? (
                <div className="text-[10px] text-[var(--jr-text-3)]/50 italic">—</div>
            ) : here.map((p) => (
                <div
                    key={p.id || p.name}
                    draggable={!!p.id}
                    onDragStart={(e) => handleDragStart(e, p)}
                    data-testid={`stakeholder-chip-${p.id || p.name}`}
                    title={i18n.t("companies.chip_tip", { name: p.name || i18n.t("companies.unknown"), role: p.role || "—" })}
                    className="text-xs leading-tight rounded-md border border-white/10 bg-[var(--jr-surface)] px-2 py-1.5 cursor-grab active:cursor-grabbing hover:border-white/25 transition-colors"
                >
                    <div className="font-medium text-white truncate">{p.name || i18n.t("companies.unknown")}</div>
                    <div className="text-[10px] text-[var(--jr-text-3)] truncate">{p.role || "—"}</div>
                </div>
            ))}
        </div>
    );
}

function StakeholderGrid({ lead, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [items, setItems] = useState(lead.stakeholders || []);
    const [saving, setSaving] = useState(false);
    const [enriching, setEnriching] = useState(false);
    const [showExcluded, setShowExcluded] = useState(false);

    useEffect(() => { setItems(lead.stakeholders || []); setEditing(false); }, [lead.id]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/leads/${lead.id}/stakeholders`, { stakeholders: items });
            toast.success(i18n.t("companies.sh_saved"));
            setEditing(false);
            onSaved?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setSaving(false); }
    };

    const enrich = async () => {
        if (!confirm(i18n.t("companies.enrich_confirm", { company: lead.company_name }))) return;
        setEnriching(true);
        try {
            const r = await api.post(`/leads/${lead.id}/enrich`);
            if (r.data.added === 0) {
                toast.info(r.data.message || i18n.t("companies.sh_none_found"));
            } else {
                toast.success(i18n.t("companies.sh_added", { count: r.data.added }));
            }
            onSaved?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setEnriching(false); }
    };

    const addPerson = () => setItems((s) => [...s, { name: "", role: "", email: "", linkedin_url: "", influence: "neutral", sentiment: "neutral", notes: "" }]);
    const patchPerson = (i, k, v) => setItems((s) => s.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
    const remove = (i) => setItems((s) => s.filter((_, idx) => idx !== i));

    const visible = (lead.stakeholders || []).filter((p) => showExcluded || !p.excluded);
    const excludedCount = (lead.stakeholders || []).filter((p) => p.excluded).length;

    const toggleExclude = async (sh) => {
        try {
            await api.patch(`/leads/${lead.id}/stakeholders/${sh.id}?excluded=${!sh.excluded}`);
            onSaved?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
    };
    const removeOne = async (sh) => {
        if (!confirm(i18n.t("companies.sh_delete_confirm", { name: sh.name }))) return;
        try { await api.delete(`/leads/${lead.id}/stakeholders/${sh.id}`); toast.success(i18n.t("common.deleted")); onSaved?.(); }
        catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
    };

    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div className="min-w-0">
                    <h3 className="font-display text-xl tracking-tight">{lead.company_name}</h3>
                    <p className="text-xs text-[var(--jr-text-2)] mt-0.5">Stakeholder power-map · {(items || []).length} contactpersonen</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={enrich} disabled={enriching} data-testid="stakeholders-enrich"
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--jr-info)]/15 text-[var(--jr-info)] border border-[var(--jr-info)]/40 text-xs font-mono uppercase tracking-[0.16em] hover:bg-[var(--jr-info)]/25 disabled:opacity-50">
                        {enriching ? <Loader2 className="animate-spin" size={11} /> : <Sparkles size={11} />} Enrich agent
                    </button>
                    {!editing ? (
                        <button onClick={() => setEditing(true)} data-testid="stakeholders-edit"
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-xs font-mono uppercase tracking-[0.16em] text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                            <Sparkles size={11} /> Bewerk
                        </button>
                    ) : (
                        <>
                            <button onClick={addPerson} data-testid="stakeholders-add"
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-xs font-mono uppercase tracking-[0.16em] text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                                <UserPlus size={11} /> {i18n.t("common.add")}
                            </button>
                            <button onClick={save} disabled={saving} data-testid="stakeholders-save"
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--jr-brand)] text-black text-xs font-medium disabled:opacity-50">
                                {saving ? <Loader2 className="animate-spin" size={11} /> : null} Bewaar
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* TheOrg org-chart + Surfe enrichment log (compact, collapsible) */}
            <div className="grid gap-3 lg:grid-cols-2 mb-5">
                <TheOrgChartPanel lead={lead}/>
                <EnrichmentLogPanel leadId={lead.id}/>
            </div>

            {/* Power-map grid: rows = Influence (high→low) × cols = Sentiment
                Sleep een stakeholder-kaart naar een andere cel om zijn
                (influence, sentiment) te updaten — PATCH gaat naar
                /leads/{id}/stakeholders/{sid}?influence=…&sentiment=… */}
            <div className="grid grid-cols-[80px_repeat(3,1fr)] gap-2">
                <div />
                {SENTIMENT.map((s) => (
                    <div key={s.id} className="text-center text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] py-1">{s.label}</div>
                ))}
                {INFLUENCE.map((inf) => (
                    <React.Fragment key={inf.id}>
                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] py-2 pr-2 text-right flex items-center justify-end gap-1" style={{ color: inf.accent }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: inf.accent }} />
                            <span>{inf.label}</span>
                        </div>
                        {SENTIMENT.map((sn) => {
                            const here = (items || []).filter((p) => !p.excluded && (p.influence || "neutral") === inf.id && (p.sentiment || "neutral") === sn.id);
                            return (
                                <PowerCell
                                    key={inf.id + sn.id}
                                    inf={inf} sn={sn} here={here}
                                    leadId={lead.id}
                                    onDropped={onSaved}
                                />
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>

            {/* Editor list */}
            {editing && (
                <div className="mt-6 space-y-2" data-testid="stakeholders-editor">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2">{i18n.t("companies.sh_edit")}</div>
                    {items.map((p, i) => (
                        <StakeholderEditRow key={i} idx={i} p={p} onChange={patchPerson} onRemove={() => remove(i)} />
                    ))}
                    {items.length === 0 && (
                        <div className="text-xs text-[var(--jr-text-3)]">{i18n.t("companies.sh_add_hint")}</div>
                    )}
                </div>
            )}

            {/* Enriched table view (matches screenshot 2 layout) */}
            {!editing && (
                <div className="mt-6" data-testid="stakeholders-table">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)] flex items-center gap-2">
                            <Sparkles size={10} /> Enriched stakeholder data
                        </div>
                        {excludedCount > 0 && (
                            <button onClick={() => setShowExcluded(!showExcluded)} data-testid="toggle-excluded"
                                className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-2)] hover:text-white">
                                {showExcluded ? "Verberg" : "Toon"} {excludedCount} excluded
                            </button>
                        )}
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-white/8">
                        <table className="w-full text-xs" data-testid="enriched-table">
                            <thead className="bg-[var(--jr-surface-2)]/60">
                                <tr className="text-left text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">
                                    <th className="px-3 py-2 w-32">Actions</th>
                                    <th className="px-3 py-2">Contact info</th>
                                    <th className="px-3 py-2">LinkedIn</th>
                                    <th className="px-3 py-2">GSM / Email</th>
                                    <th className="px-3 py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.length === 0 ? (
                                    <tr><td colSpan={5} className="px-3 py-6 text-center text-[var(--jr-text-3)] italic">
                                        Nog geen stakeholders. Klik &quot;Enrich agent&quot; om Sales Hunter te laten zoeken.
                                    </td></tr>
                                ) : visible.map((p) => (
                                    <tr key={p.id} data-testid={`row-${p.id}`}
                                        className={`border-t border-white/6 ${p.excluded ? "opacity-40" : ""}`}>
                                        <td className="px-3 py-2">
                                            <div className="flex flex-col gap-1">
                                                <button onClick={() => toggleExclude(p)} data-testid={`exclude-${p.id}`}
                                                    className={`inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded text-[10px] ${p.excluded ? "bg-[var(--jr-success)]/15 text-[var(--jr-success)]" : "bg-[var(--jr-amber)]/15 text-[var(--jr-amber)]"} hover:opacity-80`}>
                                                    {p.excluded ? "Re-include" : "Exclude"}
                                                </button>
                                                <button onClick={() => removeOne(p)} data-testid={`del-${p.id}`}
                                                    className="inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded text-[10px] bg-[var(--jr-error)]/15 text-[var(--jr-error)] hover:bg-[var(--jr-error)]/25">
                                                    <Trash2 size={9} /> Delete
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="text-sm font-medium text-white">{p.name}</div>
                                            <div className="text-[11px] text-[var(--jr-text-2)]">{p.role || "—"}</div>
                                            {p.network_degree && (
                                                <div className="mt-0.5 text-[9px] inline-flex items-center gap-1 text-[var(--jr-text-3)]">
                                                    Network: <span className="px-1 py-0.5 rounded bg-[#0a66c2]/20 text-[#0a66c2]">{p.network_degree} degree</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 max-w-[280px]">
                                            <LinkedInButton url={p.linkedin_url} name={p.name} variant="inline" className="text-[11px]" testid={`stakeholder-linkedin-${p.id}`} />
                                        </td>
                                        <td className="px-3 py-2">
                                            {p.phone && <a href={`tel:${p.phone.replace(/\s/g, "")}`} className="block text-[11px] text-[var(--jr-success)] hover:text-white">{p.phone}</a>}
                                            {p.email && <a href={`mailto:${p.email}`} className="block text-[11px] text-[var(--jr-info)] hover:text-white truncate">{p.email}</a>}
                                            {!p.phone && !p.email && <span className="text-[var(--jr-text-3)] text-[11px]">No company info</span>}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1 flex-wrap">
                                                <span className="text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-white/5 text-[var(--jr-text-2)]">{p.influence || "neutral"}</span>
                                                <span className={`text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded ${p.sentiment === "warm" ? "bg-[var(--jr-success)]/15 text-[var(--jr-success)]" : p.sentiment === "cold" ? "bg-[var(--jr-info)]/15 text-[var(--jr-info)]" : "bg-white/5 text-[var(--jr-text-2)]"}`}>
                                                    {p.sentiment || "neutral"}
                                                </span>
                                                {p.enrichment_status === "enriched" && (
                                                    <span className="text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-[var(--jr-info)]/15 text-[var(--jr-info)]">
                                                        AI-enriched
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function StakeholderEditRow({ idx, p, onChange, onRemove }) {
    return (
        <div className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
            <input data-testid={`sh-name-${idx}`} placeholder="Naam" value={p.name || ""} onChange={(e) => onChange(idx, "name", e.target.value)}
                className="md:col-span-3 bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
            <input data-testid={`sh-role-${idx}`} placeholder="Functie" value={p.role || ""} onChange={(e) => onChange(idx, "role", e.target.value)}
                className="md:col-span-3 bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
            <input data-testid={`sh-email-${idx}`} placeholder="email@..." value={p.email || ""} onChange={(e) => onChange(idx, "email", e.target.value)}
                className="md:col-span-2 bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
            <select data-testid={`sh-influence-${idx}`} value={p.influence || "neutral"} onChange={(e) => onChange(idx, "influence", e.target.value)}
                className="md:col-span-2 bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none">
                {INFLUENCE.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
            </select>
            <select data-testid={`sh-sentiment-${idx}`} value={p.sentiment || "neutral"} onChange={(e) => onChange(idx, "sentiment", e.target.value)}
                className="md:col-span-1 bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none">
                {SENTIMENT.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <button onClick={onRemove} data-testid={`sh-remove-${idx}`}
                className="md:col-span-1 rounded p-1.5 text-[var(--jr-error)] hover:bg-[var(--jr-error)]/10 self-center">
                <Trash2 size={14} />
            </button>
            <input data-testid={`sh-phone-${idx}`} placeholder="GSM" value={p.phone || ""} onChange={(e) => onChange(idx, "phone", e.target.value)}
                className="md:col-span-3 bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-xs text-[var(--jr-text-2)] outline-none focus:border-[var(--jr-brand)]" />
            <select data-testid={`sh-degree-${idx}`} value={p.network_degree || ""} onChange={(e) => onChange(idx, "network_degree", e.target.value || null)}
                className="md:col-span-2 bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-xs text-[var(--jr-text-2)] outline-none">
                <option value="">Netwerk-graad</option>
                <option value="1st">1st degree</option><option value="2nd">2nd degree</option>
                <option value="3rd">3rd degree</option><option value="outside">Outside</option>
            </select>
            <input data-testid={`sh-linkedin-${idx}`} placeholder="LinkedIn URL" value={p.linkedin_url || ""} onChange={(e) => onChange(idx, "linkedin_url", e.target.value)}
                className="md:col-span-7 bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-xs text-[var(--jr-text-2)] outline-none focus:border-[var(--jr-brand)]" />
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Create modal                                                       */
/* ------------------------------------------------------------------ */
function CompanyCreateModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        company_name: "", contact_name: "", contact_title: "",
        contact_email: "", contact_linkedin: "",
        notes: "", score: 60, stage: "new", manual_source: "",
    });
    const [stakeholders, setStakeholders] = useState([]);
    const [saving, setSaving] = useState(false);

    const addPerson = () => setStakeholders((s) => [...s, { name: "", role: "", email: "", linkedin_url: "", influence: "neutral", sentiment: "neutral" }]);
    const patchPerson = (i, k, v) => setStakeholders((s) => s.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
    const remove = (i) => setStakeholders((s) => s.filter((_, idx) => idx !== i));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.company_name.trim()) { toast.error(i18n.t("pipeline_sales.company_required")); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/leads/manual", { ...form, score: Number(form.score) || 60, stakeholders });
            toast.success(i18n.t("companies.created", { company: form.company_name }));
            onCreated?.(data);
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("companies.create_failed")); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && onClose()}>
            <form onSubmit={submit} data-testid="company-create-modal"
                className="w-full max-w-2xl bg-[var(--jr-surface)] border border-white/10 rounded-2xl p-6 jr-fadein max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="font-display text-xl tracking-tight">{i18n.t("companies.new_company")}</h3>
                        <p className="text-xs text-[var(--jr-text-2)] mt-1">{i18n.t("companies.manual_pre")} <span className="text-[var(--jr-brand)]">MANUAL</span>{i18n.t("companies.manual_post")}</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-[var(--jr-text-3)] hover:text-white"><X size={18} /></button>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Company name *" testid="cc-company">
                        <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </Field>
                    <Field label="Source" testid="cc-source">
                        <select value={form.manual_source} onChange={(e) => setForm({ ...form, manual_source: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                            <option value="">—</option>
                            <option>Referral</option><option>Conference</option><option>Inbound</option><option>LinkedIn</option><option>Outbound</option>
                        </select>
                    </Field>
                    <Field label={i18n.t("companies.lbl_contact")} testid="cc-contact">
                        <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </Field>
                    <Field label={i18n.t("companies.lbl_role")} testid="cc-title">
                        <input value={form.contact_title} onChange={(e) => setForm({ ...form, contact_title: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </Field>
                    <Field label="Email" testid="cc-email">
                        <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </Field>
                    <Field label="LinkedIn URL" testid="cc-linkedin">
                        <input value={form.contact_linkedin} onChange={(e) => setForm({ ...form, contact_linkedin: e.target.value })}
                            placeholder="https://linkedin.com/in/…"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </Field>
                    <Field label="ICP score (0-100)" testid="cc-score">
                        <input type="number" min={0} max={100} value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </Field>
                    <Field label="Stage" testid="cc-stage">
                        <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                            <option value="new">new</option><option value="contacted">contacted</option><option value="engaged">engaged</option>
                            <option value="meeting">meeting</option><option value="won">won</option><option value="lost">lost</option>
                        </select>
                    </Field>
                    <Field label={i18n.t("companies.lbl_notes")} testid="cc-notes" full>
                        <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </Field>
                </div>

                <div className="mt-5 border-t border-white/8 pt-4">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm text-white">Stakeholders <span className="text-[var(--jr-text-3)]">({stakeholders.length})</span></h4>
                        <button type="button" onClick={addPerson} data-testid="cc-add-stakeholder"
                            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.16em] px-2.5 py-1 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5">
                            <UserPlus size={11} /> Toevoegen
                        </button>
                    </div>
                    <div className="space-y-2">
                        {stakeholders.map((p, i) => (
                            <StakeholderEditRow key={i} idx={i} p={p} onChange={patchPerson} onRemove={() => remove(i)} />
                        ))}
                        {stakeholders.length === 0 && (
                            <div className="text-xs text-[var(--jr-text-3)] italic">{i18n.t("companies.sh_none")}</div>
                        )}
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <button type="button" onClick={onClose} disabled={saving}
                        className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5">{i18n.t("common.cancel")}</button>
                    <button type="submit" disabled={saving} data-testid="cc-submit"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} {i18n.t("companies.make_company")}
                    </button>
                </div>
            </form>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Company EDIT modal — PUT /leads/{id}                               */
/* ------------------------------------------------------------------ */
function CompanyEditModal({ lead, onClose, onSaved }) {
    const [form, setForm] = useState({
        contact_name: lead.contact_name || "",
        contact_title: lead.contact_title || "",
        contact_email: lead.contact_email || "",
        contact_linkedin: lead.contact_linkedin || "",
        notes: lead.notes || "",
        score: lead.score ?? 60,
        stage: lead.stage || "new",
        manual_source: lead.manual_source || "",
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put(`/leads/${lead.id}`, { ...form, score: Number(form.score) || 0 });
            toast.success(i18n.t("companies.updated", { company: lead.company_name }));
            onSaved?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || i18n.t("companies.save_failed"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && onClose()}>
            <form onSubmit={submit} data-testid="company-edit-modal"
                className="w-full max-w-2xl bg-[var(--jr-surface)] border border-white/10 rounded-2xl p-6 jr-fadein max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="font-display text-xl tracking-tight">{i18n.t("companies.edit_company")}</h3>
                        <p className="text-xs text-[var(--jr-text-2)] mt-1">
                            <span className="text-white font-medium">{lead.company_name}</span> · {i18n.t("companies.created_on")} {lead.created_at ? new Date(lead.created_at).toLocaleDateString("nl-BE") : "?"}.
                            <span className="ml-2 text-[var(--jr-text-3)]">{i18n.t("companies.name_locked")}</span>
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className="text-[var(--jr-text-3)] hover:text-white"><X size={18} /></button>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label={i18n.t("companies.lbl_contact")} testid="ce-contact">
                        <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </Field>
                    <Field label={i18n.t("companies.lbl_role")} testid="ce-title">
                        <input value={form.contact_title} onChange={(e) => setForm({ ...form, contact_title: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </Field>
                    <Field label="Email" testid="ce-email">
                        <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </Field>
                    <Field label="LinkedIn URL" testid="ce-linkedin">
                        <input value={form.contact_linkedin} onChange={(e) => setForm({ ...form, contact_linkedin: e.target.value })}
                            placeholder="https://linkedin.com/in/…"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </Field>
                    <Field label="ICP score (0-100)" testid="ce-score">
                        <input type="number" min={0} max={100} value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </Field>
                    <Field label="Stage" testid="ce-stage">
                        <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                            <option value="new">new</option><option value="contacted">contacted</option><option value="engaged">engaged</option>
                            <option value="meeting">meeting</option><option value="won">won</option><option value="lost">lost</option>
                        </select>
                    </Field>
                    <Field label="Manual source" testid="ce-source">
                        <select value={form.manual_source} onChange={(e) => setForm({ ...form, manual_source: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                            <option value="">—</option>
                            <option>Referral</option><option>Conference</option><option>Inbound</option><option>LinkedIn</option><option>Outbound</option>
                        </select>
                    </Field>
                    <Field label={i18n.t("companies.lbl_notes")} testid="ce-notes" full>
                        <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </Field>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <button type="button" onClick={onClose} disabled={saving}
                        className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5">{i18n.t("common.cancel")}</button>
                    <button type="submit" disabled={saving} data-testid="ce-submit"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {i18n.t("common.save")}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children, testid, full }) {
    return (
        <label className={`block ${full ? "sm:col-span-2" : ""}`} data-testid={testid}>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-1 block">{label}</span>
            {children}
        </label>
    );
}


/* ------------------------------------------------------------------ */
/*  TheOrg org-chart panel (new for stakeholder view)                 */
/* ------------------------------------------------------------------ */
function TheOrgChartPanel({ lead }) {
    const [state, setState] = useState({ loading: false, data: null, error: null });
    const [expanded, setExpanded] = useState(false);
    // Prefer explicit company website; fall back to the email-domain if the
    // lead has a work email. Personal LinkedIn (`/in/…`) URLs are never valid
    // for TheOrg — we only accept `/company/…` URLs as a last resort.
    const { domain, companyLinkedin } = useMemo(() => {
        let dom = null;
        const raw = (lead?.website || "").trim();
        if (raw) {
            const m = raw.replace(/^https?:\/\//i, "").replace(/^www\./i, "").split("/")[0];
            if (m && m.includes(".")) dom = m;
        }
        if (!dom && lead?.contact_email) {
            const parts = lead.contact_email.split("@");
            if (parts.length === 2 && parts[1].includes(".")) dom = parts[1].toLowerCase();
        }
        const li = (lead?.contact_linkedin || "").toLowerCase();
        const cli = li.includes("linkedin.com/company/") ? lead.contact_linkedin : null;
        return { domain: dom, companyLinkedin: cli };
    }, [lead]);

    const load = async () => {
        setState({ loading: true, data: null, error: null });
        try {
            let q = "";
            if (domain) q = `?domain=${encodeURIComponent(domain)}`;
            else if (companyLinkedin) q = `?linkedin_url=${encodeURIComponent(companyLinkedin)}`;
            else {
                setState({ loading: false, data: null,
                    error: i18n.t("companies.theorg_no_domain") });
                return;
            }
            const { data } = await api.get(`/integrations/theorg/org-chart${q}`);
            setState({ loading: false, data, error: null });
            setExpanded(true);
        } catch (e) {
            setState({ loading: false, data: null, error: e?.response?.data?.detail || i18n.t("companies.theorg_inactive") });
        }
    };
    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface-2)]/50 p-4" data-testid="theorg-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <div className="flex items-center gap-2">
                    <Users size={12} className="text-[#a855f7]"/>
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--jr-text-3)]">TheOrg · org chart</span>
                    {domain && <span className="text-[10px] text-[var(--jr-text-2)] font-mono">{domain}</span>}
                </div>
                <button onClick={load} disabled={state.loading}
                    data-testid="theorg-fetch"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] bg-[#a855f7]/15 text-[#a855f7] border border-[#a855f7]/40 hover:bg-[#a855f7]/25 disabled:opacity-50">
                    {state.loading ? <Loader2 className="animate-spin" size={10}/> : <Network size={10}/>}
                    {state.data ? i18n.t("common.refresh") : i18n.t("companies.fetch")}
                </button>
            </div>
            {state.error && (
                <div className="rounded-md border border-red-500/30 bg-red-500/[0.05] px-3 py-2 text-[11px] text-red-300">
                    <Info size={11} className="inline mr-1"/> {state.error}
                </div>
            )}
            {state.loading && !state.data && (
                <div className="text-[11px] text-[var(--jr-text-3)] italic">{i18n.t("companies.theorg_loading")}</div>
            )}
            {!state.loading && !state.error && !state.data && (
                <div className="text-[11px] text-[var(--jr-text-3)]">
                    {i18n.t("companies.theorg_hint")}
                </div>
            )}
            {state.data && (
                <div className="space-y-2">
                    {state.data.chart ? (
                        <OrgTreeNode node={state.data.chart} data-testid="theorg-tree"/>
                    ) : (
                        <div className="text-[11px] text-[var(--jr-text-2)] italic space-y-1">
                            {state.data.api_warning && state.data.api_warning.includes("402") ? (
                                <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.05] px-2.5 py-2 not-italic">
                                    <div className="text-amber-400 font-medium text-[11px]">{i18n.t("companies.theorg_credits")}</div>
                                    <div className="text-[10px] text-[var(--jr-text-2)] mt-0.5">
                                        {i18n.t("companies.theorg_topup_pre")} <a href="https://theorg.com/billing" target="_blank" rel="noreferrer" className="underline">theorg.com/billing</a> · {i18n.t("companies.theorg_topup_post")}
                                    </div>
                                </div>
                            ) : state.data.api_warning ? (
                                <div>TheOrg-API: {state.data.api_warning}</div>
                            ) : (
                                <div>{i18n.t("companies.theorg_not_found")}</div>
                            )}
                        </div>
                    )}
                    {state.data.departments && state.data.departments.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-2 border-t border-white/8">
                            {state.data.departments.slice(0, 8).map((d) => (
                                <span key={d} className="inline-block px-2 py-0.5 rounded-full text-[10px] font-mono bg-white/5 text-[var(--jr-text-2)] border border-white/10">{d}</span>
                            ))}
                            {state.data.departments.length > 8 && (
                                <span className="text-[10px] text-[var(--jr-text-3)] font-mono">+{state.data.departments.length - 8}</span>
                            )}
                        </div>
                    )}
                    {state.data.embed_url && (
                        <a href={state.data.embed_url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] text-[#a855f7] hover:underline">
                            <ExternalLink size={10}/> Bekijk volledige chart op TheOrg
                        </a>
                    )}
                    {state.data.position_count > 0 && (
                        <div className="text-[10px] text-[var(--jr-text-3)] font-mono">
                            {state.data.position_count} posities gemapped
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function OrgTreeNode({ node }) {
    // Flatten the tree into a list of (name, title, linkedin, depth) rows.
    // Iterative — no self-reference — so the visual-edits Babel plugin
    // doesn't recurse into a JSX self-call.
    const rows = [];
    const stack = [{ n: node, d: 0 }];
    let guard = 0;
    while (stack.length && guard < 400) {
        guard += 1;
        const { n, d } = stack.pop();
        if (!n) continue;
        rows.push({
            name: n.name || n.full_name || "—",
            title: n.title || "",
            linkedin_url: n.linkedin_url || "",
            depth: d,
        });
        const kids = Array.isArray(n.reports || n.children) ? (n.reports || n.children) : [];
        for (let i = kids.length - 1; i >= 0; i -= 1) {
            if (d < 4) stack.push({ n: kids[i], d: d + 1 });
        }
    }
    return (
        <div className="text-xs">
            {rows.slice(0, 40).map((r, i) => (
                <div key={i} className="flex items-center gap-1.5 py-0.5" style={{ paddingLeft: r.depth * 10 }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#a855f7]/60"/>
                    <span className="font-medium text-white truncate">{r.name}</span>
                    {r.title && <span className="text-[10px] text-[var(--jr-text-3)] truncate italic">· {r.title}</span>}
                    {r.linkedin_url && (
                        <a href={r.linkedin_url} target="_blank" rel="noopener noreferrer"
                            className="text-[var(--jr-info)] hover:text-white shrink-0" title="Open LinkedIn">
                            <Linkedin size={10}/>
                        </a>
                    )}
                </div>
            ))}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Enrichment log — shows which data provider (Surfe/Apollo/…) fired */
/* ------------------------------------------------------------------ */
function EnrichmentLogPanel({ leadId }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!leadId) return;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/enrichment-log/lead/${leadId}`);
                setRows(data.events || []);
            } catch (_) { /* silent */ }
            finally { setLoading(false); }
        })();
    }, [leadId]);
    const colors = {
        surfe: "#22c55e", apollo: "#0891b2", perplexity: "#a855f7",
        serper: "#f59e0b", unipile: "#3b82f6", manual: "#94a3b8", theorg: "#a855f7",
    };
    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface-2)]/50 p-4" data-testid="enrichment-log-panel">
            <div className="flex items-center gap-2 mb-2">
                <Database size={12} className="text-emerald-400"/>
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--jr-text-3)]">Data-verrijking · timeline</span>
                {loading && <Loader2 className="animate-spin" size={10}/>}
            </div>
            {rows.length === 0 ? (
                <div className="text-[11px] text-[var(--jr-text-3)] italic">
                    Nog geen verrijkings-events voor deze lead. Elke Surfe/Apollo/Unipile actie verschijnt hier.
                </div>
            ) : (
                <ul className="space-y-1.5 max-h-56 overflow-y-auto">
                    {rows.slice(0, 12).map((e) => (
                        <li key={e.id} className="text-[11px] leading-snug" data-testid={`enrich-event-${e.id}`}>
                            <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-[0.16em] mr-2"
                                style={{ backgroundColor: (colors[e.provider] || "#94a3b8") + "22", color: colors[e.provider] || "#94a3b8", border: `1px solid ${colors[e.provider] || "#94a3b8"}55` }}>
                                {e.provider}
                            </span>
                            <span className="text-white">{e.action?.replace(/_/g, " ")}</span>
                            {e.detail && <span className="text-[var(--jr-text-3)]"> · {e.detail.slice(0, 90)}</span>}
                            <div className="text-[9px] text-[var(--jr-text-3)] font-mono pl-1">
                                {e.actor_type === "system" ? "🤖" : "👤"} {e.actor} · {(e.created_at || "").slice(0, 16).replace("T", " ")}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

